import { inject, Service, signal } from '@angular/core';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { RoleEnum } from '@shared/enums/user';
import { Live, LiveParticipant, ConfAndLiveRights, LiveState } from '@shared/models/live';
import { Medinbox } from '../equipment/medinbox';
import { WebSocketClient } from '../realtime/web-socket-client';
import { StateEnum, LiveStateEnum } from '@shared/enums/live';
import { WebSocketActionEnum } from '@shared/enums/websocket';
import { CurrentParticipantRights } from './current-participant-rights';
import { LiveParticipants } from './live-participants';
import { CurrentUser } from '@core/user/current-user';
import { UserFeedback } from '../ui/user-feedback';
import { LiveCommand } from '../realtime/live-command';
import { Logging } from '../logging';
import { LiveApi } from './live-api';
import { syncState } from '@shared/utils/sync-state';
import { STORAGE_KEYS } from '@shared/constants/storage-keys';

type RevokeAction = WebSocketActionEnum.DRAWING | WebSocketActionEnum.LASER | WebSocketActionEnum.SHARE_SCREEN | WebSocketActionEnum.WHITEBOARD;

// TODO: Remove once link.api ManageRights() handles state cleanup server-side (see CleanupStatesForRevokedRights)
const STATE_TO_RIGHT: [StateEnum, keyof ConfAndLiveRights, RevokeAction][] = [
  [StateEnum.DRAWING, 'canDraw', WebSocketActionEnum.DRAWING],
  [StateEnum.LASER, 'canLaser', WebSocketActionEnum.LASER],
  [StateEnum.SHARE_SCREEN, 'canShareScreen', WebSocketActionEnum.SHARE_SCREEN],
  [StateEnum.WHITEBOARD, 'canWhiteboard', WebSocketActionEnum.WHITEBOARD],
];

@Service()
export class LiveSession {
  private readonly liveCommands = inject(LiveCommand);
  private readonly liveApi = inject(LiveApi);
  private readonly userService = inject(CurrentUser);
  private readonly medinboxService = inject(Medinbox);
  private readonly webSocketClient = inject(WebSocketClient);
  private readonly currentParticipantRightsService = inject(CurrentParticipantRights);
  private readonly liveParticipantsService = inject(LiveParticipants);
  private readonly messageService = inject(UserFeedback);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly logger = inject(Logging);

  public currentConferenceId: string | undefined;
  private readonly live = syncState<Live | null>(null);
  public readonly currentLive = this.live.value;
  public readonly currentLive$ = this.live.changes$;
  private readonly state = syncState<LiveState | null>(null);
  public readonly liveState = this.state.value;
  public readonly liveState$ = this.state.changes$;

  private readonly liveElapsed = signal(0);
  public readonly liveElapsedSeconds = this.liveElapsed.asReadonly();

  constructor() {
    this.restoreLiveState();
    this.persistLiveState();
    this.followCurrentLive();
    this.refreshOnSocketReconnection();
    this.trackCurrentParticipant();
    this.syncCurrentParticipantRights();
    this.reactToRightsUpdates();
  }

  private restoreLiveState(): void {
    const savedLiveState = localStorage.getItem(STORAGE_KEYS.LIVE_STATE);
    if (!savedLiveState) return;
    try {
      this.state.set(JSON.parse(savedLiveState));
    } catch {
      localStorage.removeItem(STORAGE_KEYS.LIVE_STATE);
    }
  }

  private persistLiveState(): void {
    this.state.changes$.subscribe((state) => {
      if (state) {
        localStorage.setItem(STORAGE_KEYS.LIVE_STATE, JSON.stringify(state));
      } else {
        localStorage.removeItem(STORAGE_KEYS.LIVE_STATE);
      }
    });
  }

  private followCurrentLive(): void {
    this.live.changes$.subscribe((live) => {
      if (live) {
        this.liveParticipantsService.refreshParticipantList(live);
        if (!this.medinboxService.medinboxId()) {
          this.medinboxService.setMedinboxId(live.equipmentId);
        }
      }
    });
  }

  private refreshOnSocketReconnection(): void {
    this.webSocketClient.generalSocketReconnected$.pipe(filter((reconnected) => reconnected)).subscribe(() => this.onSocketReconnected());
  }

  private trackCurrentParticipant(): void {
    this.liveParticipantsService.participantList$.subscribe((participants) => {
      this.liveParticipantsService.setCurrentLiveParticipant(
        participants.find((p) => p.uuid.toLowerCase() === this.userService.currentUserId().toLowerCase()) ?? null,
      );
    });
  }

  private syncCurrentParticipantRights(): void {
    this.liveParticipantsService.currentLiveParticipant$
      .pipe(
        filter((participant) => participant != null),
        map((participant) => {
          this.currentParticipantRightsService.updateParticipantRights(participant);
        }),
      )
      .subscribe();
  }

  private reactToRightsUpdates(): void {
    this.currentParticipantRightsService.currentPermissions$.pipe(filter((rights) => rights != null)).subscribe((rights) => this.onRightsUpdated(rights));
  }

  private hasEquipmentRights(rights: ConfAndLiveRights | null): boolean {
    return !!(
      this.medinboxService.medinboxId() &&
      rights &&
      (rights.canChangeLayout || rights.canControlCamera || rights.canControlHeadset || rights.canManageMask)
    );
  }

  // canRecord talks to the equipment (RECORD/RECORD_TIME) over the medinbox socket but does not need its configuration
  private needsMedinboxChannel(rights: ConfAndLiveRights | null): boolean {
    return this.hasEquipmentRights(rights) || !!(this.medinboxService.medinboxId() && rights?.canRecord);
  }

  private onSocketReconnected(): void {
    const liveCode = this.live.get()?.code;
    if (!liveCode) return;

    this.logger.debug('[LiveSession] Refreshing live state after reconnection');
    this.liveApi.getLive(liveCode).subscribe({
      next: (live) => {
        this.setCurrentLive(live, { persist: false });
        const rights = this.currentParticipantRightsService.currentPermissions();
        if (this.needsMedinboxChannel(rights)) {
          void this.medinboxService.initMedinboxChannelAndGetCurrentConfiguration(liveCode, this.hasEquipmentRights(rights));
        }
      },
      error: (err) => {
        console.error('[LiveSession] Failed to refresh live state after reconnection:', err);
      },
    });
  }

  private onRightsUpdated(rights: ConfAndLiveRights): void {
    if (this.needsMedinboxChannel(rights)) {
      void this.medinboxService.initMedinboxChannelAndGetCurrentConfiguration(this.live.get()?.code as string, this.hasEquipmentRights(rights));
    }
    // TODO: Remove once link.api ManageRights() handles state cleanup server-side (see CleanupStatesForRevokedRights)
    this.checkRightsAndRemoveStateIfNeeded(rights);
  }

  /** Redirects with a message when the live is not joinable: not found, ambiguous, not started or ended. */
  public async resolveLiveByCode(liveCode: string): Promise<void> {
    // SearchLive leaves out stopped and cancelled lives and answers 404, never an empty list.
    const livesSearch = await lastValueFrom(
      this.liveApi
        .searchLive(liveCode)
        .pipe(
          catchError((error: unknown) => (error instanceof HttpErrorResponse && error.status === HttpStatusCode.NotFound ? of([]) : throwError(() => error))),
        ),
    );

    if (!livesSearch) return;

    const inProgressLives = livesSearch.filter((l) => l.liveState === LiveStateEnum.InProgress);
    if (inProgressLives.length === 1) {
      const live = await lastValueFrom(this.liveApi.getLive(inProgressLives[0].code));
      if (live) {
        this.setCurrentLive(live, { persist: true });
        this.medinboxService.setMedinboxId(live.equipmentId);
      }
    } else if (inProgressLives.length > 1) {
      this.messageService.showError(this.translate.instant('LIVE.JOIN_LIVE.WRONG_SEARCH_NAME'));
      void this.router.navigate(['']);
    } else if (livesSearch.some((l) => l.liveState === LiveStateEnum.Created)) {
      this.messageService.showError(this.translate.instant('LIVE.JOIN_LIVE.NOT_STARTED'));
      void this.router.navigate(['']);
    } else {
      // Same exit as the OK of the "terminated by moderator" popup: a refresh before that OK lands here.
      this.messageService.showError(this.translate.instant('LIVE.JOIN_LIVE.ALREADY_ENDED'));
      await this.leaveLive();
      void this.router.navigate(this.userService.exitRoute());
    }
  }

  /** With persist, the live is saved for conferenceAccessGuard right after subscribers have seen it. */
  public setCurrentLive(live: Live, { persist }: { persist: boolean }): void {
    this.live.set(live);
    if (persist) {
      localStorage.setItem(STORAGE_KEYS.CURRENT_LIVE, JSON.stringify(this.live.get()));
    }
  }

  public setLiveState(state: LiveState | null): void {
    this.state.set(state);
  }

  /** Equipment-only endpoint; the backend resolves the caller from its JWT. */
  public setLiveElapsedSeconds(seconds: number): void {
    this.liveElapsed.set(seconds);
  }

  public setPublisherTransport(protocol?: string, relayProtocol?: string): Observable<void> {
    return this.liveApi.setPublisherTransport(this.live.get()?.code, protocol, relayProtocol);
  }

  public denyParticipant(participantId: string) {
    const live = this.live.get();
    if (live) {
      this.liveParticipantsService.denyParticipant(participantId, live);
    }
  }

  public canMuteUnmute(liveParticipant: LiveParticipant, currentLiveParticipant: LiveParticipant): boolean {
    if (liveParticipant.uuid.toLowerCase() === currentLiveParticipant.uuid.toLowerCase()) {
      return true;
    }
    if (this.currentParticipantRightsService.currentPermissions().canMute && liveParticipant.liveRole !== RoleEnum.Equipment) {
      return !liveParticipant.isMuted;
    }
    return false;
  }

  public liveIsInState(state: StateEnum, userId = ''): boolean {
    const liveState = this.state.get();
    if (liveState) {
      let filteredState = liveState.currentStates.filter((s) => s.stateType === state);
      if (userId !== '') {
        filteredState = filteredState.filter((s) => s.user === userId);
      }
      return filteredState.length > 0;
    }
    return false;
  }

  public liveInStateAsync(state: StateEnum, userId = ''): Observable<boolean> {
    return this.state.changes$.pipe(
      map((ls) => {
        if (ls) {
          let filteredState = ls.currentStates.filter((s) => s.stateType === state);
          if (userId !== '') {
            filteredState = filteredState.filter((s) => s.user === userId);
          }
          return filteredState.length > 0;
        }
        return false;
      }),
    );
  }

  public async leaveLive() {
    this.live.set(null);
    this.state.set(null);
    await this.webSocketClient.closeWebSockets();
    localStorage.removeItem(STORAGE_KEYS.CURRENT_LIVE);
    if (!this.userService.isEquipment()) {
      localStorage.removeItem(STORAGE_KEYS.EQUIPMENT_ID);
    }
  }

  // TODO: Remove once link.api ManageRights() handles state cleanup server-side (see CleanupStatesForRevokedRights)
  checkRightsAndRemoveStateIfNeeded(rights: ConfAndLiveRights) {
    for (const websocketAction of statesToRevoke(rights, this.state.get(), this.userService.currentUserId())) {
      // Runs from the constructor too: the live is passed so LiveSession is not looked up while it is built.
      void this.liveCommands.send(websocketAction, { start: false }, { destinationId: this.live.get()?.code });
    }
  }

  public resetState(): void {
    this.currentConferenceId = undefined;
    this.live.set(null);
    this.state.set(null);
    this.currentParticipantRightsService.resetState();
    this.liveParticipantsService.resetState();
    this.liveElapsed.set(0);
  }
}

/** The stop actions to send, one per state the user holds without the matching right. */
export function statesToRevoke(rights: ConfAndLiveRights, liveState: LiveState | null, userId: string): RevokeAction[] {
  const userStates = liveState?.currentStates.filter((s) => s.user.toLowerCase() === userId.toLowerCase()) ?? [];
  const actions: RevokeAction[] = [];
  for (const state of userStates) {
    const mapping = STATE_TO_RIGHT.find(([stateEnum]) => stateEnum === state.stateType);
    if (mapping && !rights[mapping[1]]) {
      actions.push(mapping[2]);
    }
  }
  return actions;
}
